import {Attribute, Component, ElementRef, Injector, Optional} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  StylableComponent,
  provideAsWidgetRef,
  Parent,
  propFactory as PropertiesFactory,
  register,
  styler,
  WIDGET_CONSTANTS,
  APPLY_STYLES_TYPE
} from "@wm/components-base";
import {
  find,
  forEach,
  includes,
} from "lodash-es";
import { getClonedObject, initCaps, noop, runtimeEventManager, stringStartsWith } from "@wm/core";

const DEFAULT_CLS = 'app-custom-widget app-container';
const WIDGET_CONFIG = {widgetType: 'wmx-component', hostClass: DEFAULT_CLS};
const registeredPropsSet = new Set<string>();

let wmxComponentDefaultProps = {},
  wmxComponentWidgetPropsMap: {[key: string]: any} = {},
  wmxComponentMethodsMap: {[key: string]: any}     = {},
  propsSkipList        = ['width', 'height', 'show', 'animation'],
  propertyGroups,
  propertiesGroup: any,
  eventsGroup: any = {};

@Component({
  selector: 'div[wmxComponent]',
  template: `
    <ng-container>
      <div *ngIf="iconUrl" class="app-x-component-icon" style="background-image: url({{iconUrl}});"></div>
      <i *ngIf="!iconUrl" class="wms app-x-component-label" >&lt;{{componentName}}/&gt;</i>
    </ng-container>
  `,
  providers: [
    provideAsWidgetRef(WmxComponentDirective)
  ],
  exportAs: 'wmxComponent',
  imports: [CommonModule],
  standalone: true
})
export class WmxComponentDirective extends StylableComponent {
  static initializeProps = register('wmx-component');
  componentName: string;
  private _methodsMap: any;
  private componentid: string | undefined;
  private wmxWidgetsMngr: any;
  private propPanel: any;
  private wmxPropsReady: any;
  public iconUrl: string;


  constructor(inj: Injector, @Optional() viewParent: Parent, elRef: ElementRef,
              @Attribute('componentname') componentName: string
  ) {
    let resolveFn: Function = noop;
    super(inj, WIDGET_CONFIG, viewParent, new Promise(res => resolveFn = res));
    this.componentName = componentName;
    this.wmxPropsReady = resolveFn;
    this.wmxWidgetsMngr = runtimeEventManager.getService('WmxWidgetsManager');
    this.propPanel = runtimeEventManager.getService('PropertyPanel');
    wmxComponentDefaultProps = PropertiesFactory.getWidgetProperties('wmx-component');
    styler(this.nativeElement, this, APPLY_STYLES_TYPE.CONTAINER);
    this.setComponentPropertyGroups();
  }

  setComponentPropertyGroups() {
    let groups = this.propPanel.propertyGroups,
      basicPropsSubGrp;

    let propertyGroups = groups.filter((group: any) => {

      if (group.name === 'properties') {
        propertiesGroup = group;
      } else if (group.name === 'events') {
        eventsGroup = group;
      }

      return !!group.parent;
    });

    basicPropsSubGrp = find(propertiesGroup.subGroups, (subGroup) => {
      return !subGroup.name;
    });

    propsSkipList = propsSkipList.concat(basicPropsSubGrp.properties);
  }

  onConfigLoad(config: any) {
    let widgetProps: {[key: string]: any} = {},
      userDefinedProps,
      userDefinedEvents,
      userDefinedMethods,
      layoutProps = [],
      behaviorProps = ['show', 'animation'],
      wmxComponentEvents: Array<any> = [],
      wmxComponentProperties: Array<any> = [];

    const projectService = runtimeEventManager.getService('projectService');
    this.iconUrl = config.iconUrl ? (
        '/studio/services/projects/' + projectService.getProjectId() 
        + '/resources/content/project/src/main/webapp/extensions/components/src/'
        + config.name+'/'
        + config.iconUrl)
      : '';

    const hasLayoutWidth = Object.keys(config.props).find(k => k === 'width');
    const hasLayoutHeight = Object.keys(config.props).find(k => k === 'height');
    if (hasLayoutWidth) {
      layoutProps.push('width');
    }
    if (hasLayoutHeight) {
      layoutProps.push('height');
    }
    const isExists = propertiesGroup.subGroups.find((o: any) => {return o.name === this.componentName + '_' + 'properties' && o.boundComponentName === this.componentName});

    if (!isExists) {
      propertiesGroup.subGroups.splice(1, 0, {
        'boundComponentName': this.componentName,
        'name': this.componentName + '_' + 'properties',
        'parent': 'properties',
        'properties': wmxComponentProperties,
        'filteredProps': wmxComponentProperties
      }, {
        'boundComponentName': this.componentName,
        'name': this.componentName + '_' + 'layout',
        'displayKey': 'LABEL_PROPERTYGROUP_LAYOUT',
        'parent': 'properties',
        'properties': layoutProps,
        'filteredProps': layoutProps
      }, {
        'boundComponentName': this.componentName,
        'name': this.componentName + '_' + 'behavior',
        'displayKey': 'LABEL_PROPERTYGROUP_BEHAVIOR',
        'parent': 'properties',
        'properties': behaviorProps,
        'filteredProps': behaviorProps
      });

      eventsGroup.subGroups.push({
        'boundComponentName': this.componentName,
        'name': this.componentName + '_' + 'events',
        'parent': 'events',
        'properties': wmxComponentEvents
      });

      userDefinedProps = config.props || {};
      userDefinedEvents = config.events || {};
      userDefinedMethods = config.methods || {};

      this.componentid = config.id;

      forEach(userDefinedProps, (prop, key) => {
        key = key.toLowerCase();
        if ((prop.type === undefined
          || prop.type === 'string'
          || prop.type === 'number'
          || prop.type === 'boolean') && !prop.isList) {
          prop.value = prop.defaultValue;
        }
        prop.bindable = 'in-out-bound';
        widgetProps[key] = prop;

        if (stringStartsWith(prop.value, 'bind:')) {
          prop.__value = prop.value;
          prop.value = undefined;
        }

        // prop.value = this.overrideResourcePath(prop.value);

        if (!includes(propsSkipList, key)) {
          wmxComponentProperties.push(key);
        }

        if (prop.hasOwnProperty('options') && stringStartsWith(prop.options, 'bind:')) {
          prop.__options = prop.options;
          delete prop.options;
        }

        if (prop.bindable === 'out-bound') {
          prop.show = false;
          prop.showAt = undefined;
        } else if (!prop.hasOwnProperty('show')) {
          prop.show = true;
        } else if (stringStartsWith(prop.show, 'bind:')) {
          prop.__show = prop.show;
          prop.show = true;
        }

        if (!prop.hasOwnProperty('disabled')) {
          prop.disabled = false;
        } else if (stringStartsWith(prop.disabled, 'bind:')) {
          prop.__disabled = prop.disabled;
          prop.disabled = false;
        }

        if (prop.type === 'number' && prop.widget === 'list') {
          prop.widget = 'list-number';
        }

        prop.widget = prop.type === 'date' ? 'date' : prop.widget;

        if (prop.value && prop.type === 'date') {
          prop.value = new Date(prop.value);
        }

        prop.label = initCaps(key);
        prop.helpText = prop.description;

      });

      forEach(userDefinedEvents, (prop, key) => {
        widgetProps[key] = prop;

        wmxComponentEvents.push(key);

        prop.displayName = prop.displayName ? 'On ' + prop.displayName : '';
        prop.show = true;
        prop.disabled = false;
        prop.type = 'event';
        prop.widget = 'eventlist';
        prop.options = WIDGET_CONSTANTS.EVENTS_OPTIONS;
        prop.helpText = prop.description;
      });
    }

    if (wmxComponentWidgetPropsMap[this.componentName]) {
      this.widgetProps = getClonedObject(wmxComponentWidgetPropsMap[this.componentName]);
      this._methodsMap = wmxComponentMethodsMap[this.componentName];
      runtimeEventManager.emit('wms:refresh-properties-panel');
      return;
    }

    forEach(wmxComponentDefaultProps, (prop, key) => {
      if (widgetProps[key] === undefined) {
        widgetProps[key] = prop;
      }
    });

    wmxComponentWidgetPropsMap[this.componentName] = widgetProps;
    wmxComponentMethodsMap[this.componentName]     = userDefinedMethods;

    this.widgetProps = getClonedObject(wmxComponentWidgetPropsMap[this.componentName]);
    this._methodsMap = wmxComponentMethodsMap[this.componentName];
    registeredPropsSet.add(this.widgetType);
    runtimeEventManager.emit('widgetSelection');
  }


  loadDependencies(componentName: string) {
    return this.wmxWidgetsMngr.loadWmxWidgetConfig(componentName).then(this.onConfigLoad.bind(this)).then(() => this.wmxPropsReady());
  }


  override ngOnInit() {
    super.ngOnInit();
    this.loadDependencies(this.componentName);
  }

}
